/**
 * Reusable loading skeleton components for list pages, detail pages, and cards.
 * Uses @medicorepay/ui Skeleton primitives and Tailwind animate-pulse.
 */

import { Skeleton } from "@medicorepay/ui";

interface TableSkeletonRowsProps {
  rows?: number;
  cols?: number;
}

/** Skeleton rows inside an actual <tbody> — drop-in for table loading states */
export function TableSkeletonRows({
  rows = 5,
  cols = 5,
}: TableSkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-b border-slate-100">
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="px-4 py-3">
              <div className="h-4 animate-pulse rounded bg-slate-200" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Full-page skeleton with header + stats row + table */
export function ListPageSkeleton({
  rows = 6,
  cols = 5,
}: TableSkeletonRowsProps) {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton variant="rectangular" className="h-6 w-48 rounded" />
          <Skeleton variant="rectangular" className="h-4 w-72 rounded" />
        </div>
        <Skeleton variant="rectangular" className="h-10 w-32 rounded-xl" />
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200 p-4 space-y-2"
          >
            <Skeleton variant="rectangular" className="h-3 w-20 rounded" />
            <Skeleton variant="rectangular" className="h-7 w-16 rounded" />
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="h-5 w-40 rounded bg-slate-200" />
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              {Array.from({ length: cols }).map((_, c) => (
                <th key={c} className="px-4 py-3">
                  <div className="h-3 w-20 rounded bg-slate-200" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableSkeletonRows rows={rows} cols={cols} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Detail page skeleton with header + two-column layout */
export function DetailPageSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Back + Title */}
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-slate-200" />
        <div className="space-y-1.5">
          <div className="h-6 w-56 rounded bg-slate-200" />
          <div className="h-4 w-36 rounded bg-slate-200" />
        </div>
      </div>

      {/* Two column */}
      <div className="grid gap-6 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4"
          >
            <div className="h-5 w-32 rounded bg-slate-200" />
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="flex justify-between">
                <div className="h-4 w-24 rounded bg-slate-200" />
                <div className="h-4 w-40 rounded bg-slate-200" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Card grid skeleton */
export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-200" />
            <div className="space-y-1.5 flex-1">
              <div className="h-4 w-32 rounded bg-slate-200" />
              <div className="h-3 w-20 rounded bg-slate-200" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-slate-200" />
            <div className="h-3 w-3/4 rounded bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}
